import React, { useMemo, useState } from 'react'
import SafeIcon from '../../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'

const {
  FiPlusCircle,
  FiCheckCircle,
  FiClipboard,
  FiClock,
  FiRotateCw,
  FiAward,
  FiEdit3
} = FiIcons

const defaultQuestions = [
  { id: 1, prompt: 'What is the capital of France?', type: 'multiple choice', options: ['Paris', 'Berlin', 'Rome'], answer: 'Paris', points: 5 },
  { id: 2, prompt: 'The Earth orbits the sun.', type: 'true/false', answer: 'True', points: 3 },
  { id: 3, prompt: 'Complete the sentence: Water freezes at ___ degrees Celsius.', type: 'fill-blank', answer: '0', points: 4 },
  { id: 4, prompt: 'Explain the water cycle in two sentences.', type: 'short answer', answer: '', points: 8 }
]

const AssessmentCenter = () => {
  const [questions, setQuestions] = useState(defaultQuestions)
  const [newQuestion, setNewQuestion] = useState({ prompt: '', type: 'multiple choice', options: [''], answer: '', points: 1 })
  const [shortAnswers, setShortAnswers] = useState([
    { student: 'Ava M.', response: 'Evaporation, condensation, precipitation.', score: null },
    { student: 'Liam J.', response: 'Water goes up, forms clouds, then rains.', score: null }
  ])
  const [retakePolicy, setRetakePolicy] = useState({ allowed: true, attempts: 2, cooldown: 24 })
  const [results, setResults] = useState([
    { student: 'Ava M.', objectiveScore: 12, subjectiveScore: 7, total: 19, status: 'Needs Manual Review' },
    { student: 'Liam J.', objectiveScore: 10, subjectiveScore: 8, total: 18, status: 'Ready to Release' }
  ])

  const addQuestion = () => {
    if (!newQuestion.prompt) return
    const nextQuestion = {
      ...newQuestion,
      id: questions.length + 1,
      options: newQuestion.type === 'multiple choice' ? newQuestion.options.filter(Boolean) : []
    }
    setQuestions(prev => [...prev, nextQuestion])
    setNewQuestion({ prompt: '', type: 'multiple choice', options: [''], answer: '', points: 1 })
  }

  const updateShortAnswerScore = (index, score) => {
    const updated = [...shortAnswers]
    updated[index].score = score
    setShortAnswers(updated)
    setResults(current =>
      current.map(result => result.student === updated[index].student
        ? { ...result, subjectiveScore: score || 0, status: score ? 'Graded' : 'Needs Manual Review' }
        : result
      )
    )
  }

  const totalPoints = useMemo(() => questions.reduce((sum, q) => sum + (q.points || 0), 0), [questions])

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-[#00AAFF] to-[#1B4C87] rounded-2xl p-8 text-white shadow-lg">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm uppercase tracking-wider text-blue-100">Assessments</p>
            <h1 className="text-3xl font-bold">Quiz & Test System</h1>
            <p className="text-blue-100 mt-2">Build, grade, and record quizzes directly within lessons.</p>
          </div>
          <SafeIcon icon={FiClipboard} className="w-12 h-12 text-white opacity-80" />
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <div className="bg-white rounded-xl shadow-md p-6 xl:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gray-800 flex items-center">
              <SafeIcon icon={FiPlusCircle} className="w-6 h-6 mr-2 text-[#00AAFF]" />
              Quiz Builder
            </h2>
            <span className="text-sm text-gray-500">Objective auto-graded, subjective manual</span>
          </div>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-2">
                <label className="text-sm font-medium text-gray-700">Question Prompt</label>
                <input
                  type="text"
                  value={newQuestion.prompt}
                  onChange={(e) => setNewQuestion({ ...newQuestion, prompt: e.target.value })}
                  className="mt-1 w-full border border-gray-200 rounded-lg px-4 py-2 focus:ring-2 focus:ring-[#00AAFF] focus:border-transparent"
                  placeholder="Enter your question"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Type</label>
                <select
                  value={newQuestion.type}
                  onChange={(e) => setNewQuestion({ ...newQuestion, type: e.target.value })}
                  className="mt-1 w-full border border-gray-200 rounded-lg px-4 py-2 focus:ring-2 focus:ring-[#00AAFF] focus:border-transparent"
                >
                  <option value="multiple choice">Multiple Choice</option>
                  <option value="true/false">True/False</option>
                  <option value="fill-blank">Fill in the Blank</option>
                  <option value="short answer">Short Answer</option>
                </select>
              </div>
            </div>

            {newQuestion.type === 'multiple choice' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {newQuestion.options.map((option, idx) => (
                  <input
                    key={idx}
                    type="text"
                    value={option}
                    onChange={(e) => {
                      const nextOptions = [...newQuestion.options]
                      nextOptions[idx] = e.target.value
                      setNewQuestion({ ...newQuestion, options: nextOptions })
                    }}
                    className="border border-gray-200 rounded-lg px-4 py-2 focus:ring-2 focus:ring-[#00AAFF] focus:border-transparent"
                    placeholder={`Option ${idx + 1}`}
                  />
                ))}
                <button
                  className="text-[#00AAFF] text-sm font-medium"
                  onClick={() => setNewQuestion({ ...newQuestion, options: [...newQuestion.options, ''] })}
                >
                  + Add Option
                </button>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-2">
                <label className="text-sm font-medium text-gray-700">Answer</label>
                <input
                  type="text"
                  value={newQuestion.answer}
                  onChange={(e) => setNewQuestion({ ...newQuestion, answer: e.target.value })}
                  className="mt-1 w-full border border-gray-200 rounded-lg px-4 py-2 focus:ring-2 focus:ring-[#00AAFF] focus:border-transparent"
                  placeholder="Enter correct answer"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Points</label>
                <input
                  type="number"
                  min="1"
                  value={newQuestion.points}
                  onChange={(e) => setNewQuestion({ ...newQuestion, points: Number(e.target.value) })}
                  className="mt-1 w-full border border-gray-200 rounded-lg px-4 py-2 focus:ring-2 focus:ring-[#00AAFF] focus:border-transparent"
                />
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-600">Total Points: <span className="font-semibold text-gray-900">{totalPoints}</span></div>
              <button
                onClick={addQuestion}
                className="inline-flex items-center px-4 py-2 bg-[#00AAFF] text-white rounded-lg hover:bg-[#0088cc] transition-colors"
              >
                <SafeIcon icon={FiPlusCircle} className="w-4 h-4 mr-2" />
                Add Question
              </button>
            </div>

            <div className="mt-4 border-t border-gray-100 pt-4 space-y-3">
              {questions.map((question) => (
                <div key={question.id} className="p-4 bg-gray-50 rounded-lg flex items-start justify-between">
                  <div>
                    <p className="font-semibold text-gray-800">{question.prompt}</p>
                    <p className="text-sm text-gray-500 capitalize">{question.type}</p>
                    {question.type === 'multiple choice' && (
                      <p className="text-sm text-gray-600 mt-1">Options: {question.options.join(', ')}</p>
                    )}
                    {question.answer && (
                      <p className="text-sm text-gray-600 mt-1">Answer: <span className="font-medium text-gray-900">{question.answer}</span></p>
                    )}
                  </div>
                  <span className="text-[#1B4C87] font-semibold">{question.points} pts</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-md p-6 space-y-4">
          <h2 className="text-xl font-semibold text-gray-800 flex items-center">
            <SafeIcon icon={FiRotateCw} className="w-6 h-6 mr-2 text-[#00AAFF]" />
            Retake Options
          </h2>
          <div className="space-y-3">
            <label className="flex items-center space-x-3">
              <input
                type="checkbox"
                checked={retakePolicy.allowed}
                onChange={(e) => setRetakePolicy({ ...retakePolicy, allowed: e.target.checked })}
                className="w-5 h-5 rounded border-gray-300 text-[#00AAFF] focus:ring-[#00AAFF]"
              />
              <span className="text-gray-700">Allow quiz retakes</span>
            </label>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <p className="text-sm text-gray-600">Max Attempts</p>
                <input
                  type="number"
                  min="1"
                  value={retakePolicy.attempts}
                  onChange={(e) => setRetakePolicy({ ...retakePolicy, attempts: Number(e.target.value) })}
                  className="mt-1 w-full border border-gray-200 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#00AAFF] focus:border-transparent"
                />
              </div>
              <div>
                <p className="text-sm text-gray-600">Cooldown (hours)</p>
                <input
                  type="number"
                  min="0"
                  value={retakePolicy.cooldown}
                  onChange={(e) => setRetakePolicy({ ...retakePolicy, cooldown: Number(e.target.value) })}
                  className="mt-1 w-full border border-gray-200 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#00AAFF] focus:border-transparent"
                />
              </div>
            </div>
            <div className="p-3 bg-blue-50 rounded-lg text-sm text-[#1B4C87]">
              Retakes are logged to student progress along with timestamps and scores.
            </div>
          </div>

          <div className="pt-2 border-t border-gray-100">
            <h3 className="text-lg font-semibold text-gray-800 mb-3">Auto-Grading Preview</h3>
            <div className="space-y-3 text-sm text-gray-700">
              <p className="flex items-center"><SafeIcon icon={FiCheckCircle} className="w-4 h-4 mr-2 text-green-500" />Multiple choice, true/false, and fill-blank questions are auto-graded instantly.</p>
              <p className="flex items-center"><SafeIcon icon={FiClock} className="w-4 h-4 mr-2 text-[#00AAFF]" />Short answer responses are queued for manual review.</p>
              <p className="flex items-center"><SafeIcon icon={FiAward} className="w-4 h-4 mr-2 text-[#1B4C87]" />Scores sync to student progress and gradebook entries.</p>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl shadow-md p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gray-800 flex items-center">
              <SafeIcon icon={FiEdit3} className="w-6 h-6 mr-2 text-[#00AAFF]" />
              Manual Grading
            </h2>
            <span className="text-sm text-gray-500">Short Answer Queue</span>
          </div>
          <div className="space-y-3">
            {shortAnswers.map((entry, idx) => (
              <div key={entry.student} className="p-4 border border-gray-100 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <p className="font-semibold text-gray-800">{entry.student}</p>
                    <p className="text-sm text-gray-500">Response: {entry.response}</p>
                  </div>
                  <div className="flex items-center space-x-2">
                    <input
                      type="number"
                      min="0"
                      max="10"
                      value={entry.score ?? ''}
                      onChange={(e) => updateShortAnswerScore(idx, Number(e.target.value))}
                      className="w-20 border border-gray-200 rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#00AAFF] focus:border-transparent"
                      placeholder="Score"
                    />
                    <span className="text-sm text-gray-500">/10</span>
                  </div>
                </div>
                <p className="text-sm text-gray-600">Feedback is saved to student records for parent/teacher review.</p>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-md p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gray-800 flex items-center">
              <SafeIcon icon={FiAward} className="w-6 h-6 mr-2 text-[#00AAFF]" />
              Quiz Results & Progress
            </h2>
            <span className="text-sm text-gray-500">Auto-saved</span>
          </div>
          <div className="space-y-3">
            {results.map((result) => (
              <div key={result.student} className="p-4 bg-gray-50 rounded-lg flex items-center justify-between">
                <div>
                  <p className="font-semibold text-gray-800">{result.student}</p>
                  <p className="text-sm text-gray-600">Objective: {result.objectiveScore} pts • Subjective: {result.subjectiveScore} pts</p>
                  <p className="text-sm text-gray-500">Status: <span className="font-medium text-[#1B4C87]">{result.status}</span></p>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-[#00AAFF]">{result.total}</p>
                  <p className="text-xs text-gray-500">Saved to progress</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

export default AssessmentCenter
